"use client";
import "./dashboard.css";
import { useEffect, useState, useMemo } from "react";
import { Chart } from "primereact/chart";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { useQuery } from "react-query";
import api from "../../services/api";
import { transformDayToHour } from "../../utils/transformer";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Skeleton } from "primereact/skeleton";

export default function TVDashboard() {
  const currentDate = new Date();
  const firstDayOfYear = new Date(new Date().getFullYear(), 0, 1);

  const [selectClient, setSelectClient] = useState(null);
  const [currentClientIndex, setCurrentClientIndex] = useState(0);
  const [beginDate] = useState(firstDayOfYear);
  const [endDate] = useState(currentDate);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isAutoRotateEnabled, setIsAutoRotateEnabled] = useState(true);

  // Update time every second
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const sumServiceCallsStatus = (status) => {
    if (!selectClient || !selectClient?.equipments) return 0;
    let sum = 0;
    for (const equipment of selectClient?.equipments) {
      sum += equipment.service_calls.filter(
        (call) => call?.status === status
      ).length;
    }
    return sum;
  };

  const { data, isLoading } = useQuery({
    queryKey: ["companies-tv", beginDate, endDate],
    queryFn: async () =>
      await api.get(
        `companies-dashboard?beginDate=${beginDate
          .toISOString()
          .slice(0, 10)}&endDate=${endDate?.toISOString().slice(0, 10) || ""}`
      ),
    refetchInterval: 300000, // Refetch every 5 minutes
    refetchOnWindowFocus: true,
    onSuccess: (data) => {
      if (data.data.length > 0 && !selectClient) {
        setSelectClient(data.data[0]);
      }
    }
  });

  const { data: serviceCallsMonthlyData } = useQuery({
    queryKey: ["serviceCallsMonthly-tv", selectClient, endDate],
    queryFn: async () =>
      await api.get(
        `companies-dashboard/month-service-calls?company_id=${
          selectClient?.id
        }&endDate=${endDate.toISOString().slice(0, 10)}`
      ),
    enabled: !!selectClient
  });

  const { data: averageDaysData } = useQuery({
    queryKey: ["averageDaysData-tv", selectClient, beginDate, endDate],
    queryFn: async () =>
      await api.get(
        `companies-dashboard/average-days?company_id=${
          selectClient?.id
        }&beginDate=${beginDate.toISOString().slice(0, 10)}&endDate=${endDate
          .toISOString()
          .slice(0, 10)}`
      ),
    enabled: !!selectClient
  });

  const companies = data?.data ?? [];

  // Initialize first client when data loads
  useEffect(() => {
    if (companies.length > 0 && !selectClient) {
      setSelectClient(companies[0]);
    }
  }, [companies, selectClient]);

  // Auto-rotate clients every 30 seconds (only if enabled)
  useEffect(() => {
    if (companies.length <= 1 || !isAutoRotateEnabled) return;

    const interval = setInterval(() => {
      setCurrentClientIndex((prevIndex) => (prevIndex + 1) % companies.length);
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [companies.length, isAutoRotateEnabled]);

  // Update selected client when index changes
  useEffect(() => {
    if (companies.length > 0 && companies[currentClientIndex]) {
      setSelectClient(companies[currentClientIndex]);
    }
  }, [currentClientIndex, companies]);

  // Handle manual company selection
  const handleCompanyChange = (e) => {
    const newClient = e.value;
    setSelectClient(newClient);
    const newIndex = companies.findIndex((c) => c.id === newClient.id);
    if (newIndex !== -1) {
      setCurrentClientIndex(newIndex);
    }
  };

  // Toggle auto-rotation
  const toggleAutoRotate = () => {
    setIsAutoRotateEnabled(!isAutoRotateEnabled);
  };

  // Prepare doughnut chart data - memoized to prevent flickering
  const doughnutChartData = useMemo(() => {
    const statusAberto = sumServiceCallsStatus("Aberto");
    const statusEmDeslocamento = sumServiceCallsStatus("Em deslocamento");
    const statusEmAndamento = sumServiceCallsStatus("Em andamento");
    const statusFechado = sumServiceCallsStatus("Fechado");
    const statusCancelado = sumServiceCallsStatus("Cancelado");
    const statusPendente =
      sumServiceCallsStatus("Pendente") +
      sumServiceCallsStatus("Pendente peça");

    return {
      labels: ["Aberto", "Em deslocamento", "Em Andamento", "Fechado", "Cancelado", "Pendente"],
      datasets: [
        {
          data: [
            statusAberto,
            statusEmDeslocamento,
            statusEmAndamento,
            statusFechado,
            statusCancelado,
            statusPendente
          ],
          backgroundColor: [
            "#f44336",
            "#1565C0",
            "#FFC107",
            "#4CAF50",
            "#607D8B",
            "#FF9800"
          ],
          borderWidth: 4,
          borderColor: "#0f172a"
        }
      ]
    };
  }, [selectClient]);

  const doughnutChartOptions = useMemo(
    () => ({
      cutout: "70%",
      plugins: {
        legend: {
          position: "right",
          labels: {
            padding: 20,
            font: {
              size: 16,
              weight: "600"
            },
            usePointStyle: true,
            pointStyle: "circle",
            color: "#fff"
          }
        },
        datalabels: {
          formatter: (value) => (value > 0 ? value : ""),
          color: "#fff",
          font: {
            weight: "bold",
            size: 20
          }
        }
      }
    }),
    []
  );

  // Prepare bar chart data - memoized to prevent flickering
  const barChartData = useMemo(() => {
    const sortedEquipment = selectClient?.equipments
      ? [...selectClient.equipments]
          .filter((eq) => eq.service_calls?.length > 0)
          .sort(
            (a, b) =>
              (b.service_calls?.length || 0) - (a.service_calls?.length || 0)
          )
          .slice(0, 10)
      : [];

    return {
      labels: sortedEquipment.map((equipment) => {
        const name = equipment?.pat || "Sem nome";
        return name.length > 20 ? name.substring(0, 17) + "..." : name;
      }),
      datasets: [
        {
          label: "Quantidade",
          backgroundColor: "#26a69a",
          data: sortedEquipment.map(
            (equipment) => equipment?.service_calls?.length || 0
          ),
          borderRadius: 8,
          datalabels: {
            align: "end",
            anchor: "end",
            formatter: (value) => value,
            color: "#fff",
            font: {
              weight: "bold",
              size: 16
            }
          }
        }
      ]
    };
  }, [selectClient]);

  const barChartOptions = useMemo(
    () => ({
      indexAxis: "y",
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        }
      },
      scales: {
        x: {
          ticks: {
            stepSize: 1,
            font: {
              size: 14
            },
            color: "#fff",
            callback: function (value) {
              return Number.isInteger(value) ? value : null;
            }
          },
          grid: {
            color: "rgba(255, 255, 255, 0.1)",
            drawBorder: false
          }
        },
        y: {
          ticks: {
            font: {
              size: 14,
              weight: "600"
            },
            color: "#fff"
          },
          grid: {
            display: false,
            drawBorder: false
          }
        }
      }
    }),
    []
  );

  // Prepare line chart data - memoized to prevent flickering
  const lineChartData = useMemo(() => {
    if (!serviceCallsMonthlyData) return { labels: [], datasets: [] };

    const currentMonth = new Date().getMonth();
    const filteredMonthlyData = serviceCallsMonthlyData?.data?.filter(
      (i, index) => index <= currentMonth
    );

    return {
      labels: filteredMonthlyData?.map((i) => i?.month?.substr(0, 3)),
      datasets: [
        {
          label: "Quantidade",
          data: filteredMonthlyData?.map((i) => i?.count),
          fill: true,
          tension: 0.4,
          borderColor: "#eca81f",
          backgroundColor: "rgba(236, 168, 31, 0.2)",
          borderWidth: 3,
          pointRadius: 6,
          pointHoverRadius: 8,
          pointBackgroundColor: "#eca81f",
          pointBorderColor: "#fff",
          pointBorderWidth: 2,
          datalabels: {
            align: "top",
            anchor: "end",
            formatter: (value) => value,
            color: "#fff",
            font: {
              weight: "bold",
              size: 16
            }
          }
        }
      ]
    };
  }, [serviceCallsMonthlyData]);

  const lineChartOptions = useMemo(
    () => ({
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        }
      },
      scales: {
        x: {
          ticks: {
            color: "#fff",
            font: {
              size: 16,
              weight: "600"
            }
          },
          grid: {
            display: false
          }
        },
        y: {
          ticks: {
            color: "#fff",
            font: {
              size: 16
            },
            stepSize: 1
          },
          grid: {
            color: "rgba(255, 255, 255, 0.1)"
          }
        }
      }
    }),
    []
  );

  const sumServiceCallsLength = () => {
    if (!selectClient || !selectClient?.equipments) return 0;
    let sum = 0;
    for (const equipment of selectClient?.equipments) {
      sum += equipment.service_calls.length;
    }
    return sum;
  };

  const MetricSkeleton = () => (
    <div className="tv-metric-skeleton">
      <Skeleton width="100%" height="100%" borderRadius="16px" />
    </div>
  );

  const ChartSkeleton = () => (
    <div className="tv-chart-skeleton">
      <Skeleton width="100%" height="100%" borderRadius="16px" />
    </div>
  );

  return (
    <div className="tv-dashboard">
      {/* Header Bar */}
      <div className="tv-header-bar">
        <div className="tv-brand-section">
          <div className="tv-company-name">
            {selectClient?.name || "Carregando..."}
          </div>
          <div className="tv-company-subtitle">Monitoramento em Tempo Real</div>
        </div>

        {/* Control Panel */}
        {companies.length > 1 && (
          <div className="tv-control-panel">
            <Dropdown
              value={selectClient}
              options={companies}
              onChange={handleCompanyChange}
              optionLabel="name"
              placeholder="Selecionar cliente"
              filter
              filterPlaceholder="Buscar cliente..."
              emptyFilterMessage="Nenhum cliente encontrado"
              className="tv-company-dropdown"
              panelClassName="tv-dropdown-panel"
            />
            <Button
              icon={isAutoRotateEnabled ? "pi pi-pause" : "pi pi-play"}
              className="tv-rotate-button"
              onClick={toggleAutoRotate}
              tooltip={
                isAutoRotateEnabled ? "Pausar rotação" : "Iniciar rotação"
              }
              tooltipOptions={{ position: "bottom" }}
            />
          </div>
        )}

        <div className="tv-datetime-section">
          <div className="tv-date-display">
            {currentTime.toLocaleDateString("pt-BR", {
              weekday: "long",
              day: "2-digit",
              month: "long",
              year: "numeric"
            })}
          </div>
          <div className="tv-time-display">
            {currentTime.toLocaleTimeString("pt-BR", {
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit"
            })}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="tv-content-area">
        {/* Metrics Row */}
        <div className="tv-metrics-section">
          {isLoading ? (
            <>
              <MetricSkeleton />
              <MetricSkeleton />
              <MetricSkeleton />
            </>
          ) : (
            <>
              <div className="tv-metric-card">
                <div
                  className="tv-metric-icon"
                  style={{
                    background:
                      "linear-gradient(135deg, #2196F3 0%, #1976D2 100%)"
                  }}
                >
                  <i className="pi pi-ticket" />
                </div>
                <div className="tv-metric-content">
                  <div className="tv-metric-value">
                    {sumServiceCallsLength()}
                  </div>
                  <div className="tv-metric-label">Total de Chamados</div>
                </div>
              </div>

              <div className="tv-metric-card">
                <div
                  className="tv-metric-icon"
                  style={{
                    background:
                      "linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)"
                  }}
                >
                  <i className="pi pi-stopwatch" />
                </div>
                <div className="tv-metric-content">
                  <div className="tv-metric-value">
                    {averageDaysData?.data?.average_days_to_first_report
                      ? `${transformDayToHour(
                          averageDaysData?.data?.average_days_to_first_report
                        )}h`
                      : "0h"}
                  </div>
                  <div className="tv-metric-label">Prazo Médio Atendimento</div>
                </div>
              </div>

              <div className="tv-metric-card">
                <div
                  className="tv-metric-icon"
                  style={{
                    background:
                      "linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)"
                  }}
                >
                  <i className="pi pi-check-circle" />
                </div>
                <div className="tv-metric-content">
                  <div className="tv-metric-value">
                    {averageDaysData?.data?.average_days_to_close
                      ? `${transformDayToHour(
                          averageDaysData?.data?.average_days_to_close
                        )}h`
                      : "0h"}
                  </div>
                  <div className="tv-metric-label">Prazo Médio Resolução</div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Charts Grid */}
        <div className="tv-charts-section">
          {isLoading ? (
            <>
              <ChartSkeleton />
              <ChartSkeleton />
            </>
          ) : (
            <>
              <div className="tv-chart-container">
                <div className="tv-chart-title">
                  <i className="pi pi-chart-pie" />
                  Status dos Chamados
                </div>
                <div className="tv-chart-body">
                  <Chart
                    type="doughnut"
                    data={doughnutChartData}
                    options={doughnutChartOptions}
                    plugins={[ChartDataLabels]}
                  />
                </div>
              </div>

              <div className="tv-chart-container">
                <div className="tv-chart-title">
                  <i className="pi pi-cog" />
                  Top 10 Equipamentos
                </div>
                <div className="tv-chart-body">
                  <Chart
                    type="bar"
                    data={barChartData}
                    options={barChartOptions}
                    plugins={[ChartDataLabels]}
                  />
                </div>
              </div>
            </>
          )}
        </div>

        {/* Bottom Full-Width Chart */}
        <div className="tv-bottom-section">
          {isLoading ? (
            <ChartSkeleton />
          ) : (
            <div className="tv-chart-container">
              <div className="tv-chart-title">
                <i className="pi pi-calendar" />
                Evolução Mensal de Chamados
              </div>
              <div className="tv-chart-body">
                <Chart
                  type="line"
                  data={lineChartData}
                  options={lineChartOptions}
                  plugins={[ChartDataLabels]}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer Indicators */}
      {companies.length > 1 && (
        <div className="tv-footer-bar">
          <div className="tv-rotation-indicators">
            {companies.map((company, index) => (
              <div
                key={index}
                className={`tv-indicator-dot ${
                  index === currentClientIndex ? "tv-indicator-active" : ""
                }`}
                title={company.name}
              />
            ))}
          </div>
          <div className="tv-rotation-label">
            {isAutoRotateEnabled ? (
              <>
                <i className="pi pi-sync" style={{ marginRight: "0.5rem" }} />
                Rotação automática • 30 segundos
              </>
            ) : (
              <>
                <i className="pi pi-pause" style={{ marginRight: "0.5rem" }} />
                Rotação pausada
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
